import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "../../../ui/buttons/Button";
import LoadingScreen from "../../../ui/loadingScreen/LoadingScreen";

import useLeadWhatsApp from "./useLeadWhatsApp";
import WhatsAppConversation from "./WhatsAppConversation";
import WhatsAppMediaAttachment from "./WhatsAppMediaAttachment";
import WhatsAppNewThreadForm from "./WhatsAppNewThreadForm";
import WhatsAppTemplatePicker from "./WhatsAppTemplatePicker";
import { LINKED_ACCOUNTS_SETTINGS, whatsappTemplatesPath } from "../../../utils/whatsappRoutes";
import WhatsAppThreadList from "./WhatsAppThreadList";

import styles from "./LeadWhatsApp.module.css";

export const NO_NUMBER_TITLE = "Este lead todavía no tiene WhatsApp";
export const NO_NUMBER_TEXT =
  "Conectá el número de WhatsApp Business que atiende a este lead y las conversaciones van a aparecer acá.";
export const CONNECT_NUMBER_LABEL = "Conectar número de WhatsApp";
export const CONNECT_NUMBER_ROUTE = LINKED_ACCOUNTS_SETTINGS;

const defaultRenderMedia = (message) =>
  message?.media ? <WhatsAppMediaAttachment message={message} /> : null;

const LeadWhatsAppPanels = ({
  leadId,
  accountId = null,
  responsibleMailAccountId = null,
  initialThreadId = null,
  columnClassNames = {},
  tabs = null,
  numberAvailable = true,
  availabilityLoading = false,
  onConnectNumber,
  renderComposerActions,
  renderMedia,
}) => {
  const navigate = useNavigate();
  const whatsapp = useLeadWhatsApp({
    leadId,
    initialThreadId,
    enabled: numberAvailable && !availabilityLoading,
  });
  const [composingNew, setComposingNew] = useState(false);
  const [pickingTemplate, setPickingTemplate] = useState(false);
  const openPickerOnNextSelectionRef = useRef(false);

  useEffect(() => {
    setComposingNew(false);
    setPickingTemplate(false);
  }, [leadId]);

  useEffect(() => {
    setPickingTemplate(openPickerOnNextSelectionRef.current);
    openPickerOnNextSelectionRef.current = false;
  }, [whatsapp.selectedThreadId]);

  const handleSelectThread = (thread) => {
    setComposingNew(false);
    whatsapp.selectThread(thread);
  };

  const handleSendTemplate = async (payload) => {
    const message = await whatsapp.sendTemplate(payload);
    setPickingTemplate(false);
    return message;
  };

  const templateButton = whatsapp.selectedThread ? (
    <Button
      variant={whatsapp.selectedThread.window_open ? "secondary" : "primary"}
      tone="green"
      onClick={() => setPickingTemplate((open) => !open)}
      disabled={whatsapp.sending || whatsapp.messagesLoading}
    >
      {whatsapp.selectedThread.window_open ? "Plantilla" : "Elegir plantilla"}
    </Button>
  ) : null;

  const handleCreateThread = async (values) => {
    const previousThreadId = whatsapp.selectedThreadId;
    openPickerOnNextSelectionRef.current = true;
    try {
      const thread = await whatsapp.createThread(values);
      setComposingNew(false);
      const shouldPickTemplate = !thread?.window_open;
      const selectionChanged = Number(thread?.id) !== Number(previousThreadId);
      if (!selectionChanged || !shouldPickTemplate) {
        openPickerOnNextSelectionRef.current = false;
        setPickingTemplate(shouldPickTemplate);
      }
      return thread;
    } catch (requestError) {
      openPickerOnNextSelectionRef.current = false;
      throw requestError;
    }
  };

  const handleManageTemplates = () => {
    const connectionId = whatsapp.selectedThread?.whatsapp_account?.id;
    navigate(whatsappTemplatesPath(connectionId));
  };

  const handleOpenExisting = async (threadId) => {
    setComposingNew(false);
    const existing = whatsapp.threads.find(
      (thread) => Number(thread.id) === Number(threadId),
    );
    if (existing) {
      whatsapp.selectThread(existing);
      return;
    }
    const results = await whatsapp.reloadThreads();
    const loaded = (results || []).find(
      (thread) => Number(thread.id) === Number(threadId),
    );
    if (loaded) whatsapp.selectThread(loaded);
  };

  const handleConnectNumber = () => {
    if (typeof onConnectNumber === "function") {
      onConnectNumber();
      return;
    }
    navigate(CONNECT_NUMBER_ROUTE);
  };

  if (availabilityLoading || !numberAvailable) {
    return (
      <>
        <div className={columnClassNames.middle}>
          {tabs}
          {availabilityLoading ? (
            <LoadingScreen embedded mainText="Buscando el número de WhatsApp..." subText="" />
          ) : (
            <div className={styles.emptyState}>
              Sin número de WhatsApp conectado.
            </div>
          )}
        </div>
        <div className={columnClassNames.right}>
          {!availabilityLoading && (
            <section className={styles.noNumberPanel}>
              <h3 className={styles.noNumberTitle}>{NO_NUMBER_TITLE}</h3>
              <p className={styles.noNumberText}>{NO_NUMBER_TEXT}</p>
              <Button variant="primary" tone="green" onClick={handleConnectNumber}>
                {CONNECT_NUMBER_LABEL}
              </Button>
            </section>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className={columnClassNames.middle}>
        {tabs}
        <WhatsAppThreadList
          threads={whatsapp.threads}
          loading={whatsapp.threadsLoading}
          error={whatsapp.threadsError}
          selectedThreadId={composingNew ? null : whatsapp.selectedThreadId}
          onSelect={handleSelectThread}
          onNewConversation={() => setComposingNew(true)}
        />
      </div>

      <div className={columnClassNames.right}>
        {composingNew ? (
          <WhatsAppNewThreadForm
            accountId={accountId}
            responsibleMailAccountId={responsibleMailAccountId}
            leadId={leadId}
            creating={whatsapp.creating}
            onCreate={handleCreateThread}
            onCancel={() => setComposingNew(false)}
            onOpenExisting={handleOpenExisting}
          />
        ) : (
          <WhatsAppConversation
            thread={whatsapp.selectedThread}
            messages={whatsapp.messages}
            loading={whatsapp.messagesLoading}
            error={whatsapp.messagesError}
            hasMore={whatsapp.hasMore}
            loadingOlder={whatsapp.loadingOlder}
            onLoadOlder={whatsapp.loadOlder}
            onSend={whatsapp.sendText}
            onSendMedia={whatsapp.sendMedia}
            sending={whatsapp.sending}
            sendError={whatsapp.sendError}
            onClearSendError={whatsapp.clearSendError}
            composerActions={
              <>
                {templateButton}
                {typeof renderComposerActions === "function"
                  ? renderComposerActions(whatsapp)
                  : null}
              </>
            }
            overlay={
              pickingTemplate && whatsapp.selectedThread ? (
                <WhatsAppTemplatePicker
                  connectionId={whatsapp.selectedThread.whatsapp_account?.id ?? null}
                  threadId={whatsapp.selectedThread.id}
                  leadId={leadId}
                  onSend={handleSendTemplate}
                  sending={whatsapp.sending}
                  onClose={() => setPickingTemplate(false)}
                  onManageTemplates={handleManageTemplates}
                />
              ) : null
            }
            renderMedia={renderMedia ?? defaultRenderMedia}
          />
        )}
      </div>
    </>
  );
};

export default LeadWhatsAppPanels;
